<template>
    <div>
      <slot>
        <!-- Default popup opener element -->
        <div @click="openPopup()"
          class="datepicker-popup">
          <div class="date-wrap">
            <div class="date-label">
              {{ dateLabel }}
            </div>
            <div class="selected-date">
              {{ selectedDate }}
            </div>
          </div>
          <i class="icon icon-calendar"/>
        </div>
      </slot>

      <!-- Popup -->
      <div v-if="show || stateShow"
        class="datepicker-wrapper">
        <div class="popup-container">
          <!-- Header -->
          <div class="row header">
            <div class="vertical-border">
              <span class="popup-title">
                {{ headerLabel }}
              </span>
              <i class="popup-close icon icon-close-a"
                @click="closePopup()" />
            </div>
          </div>
          <!-- M/D/Y inputs -->
          <div class="row date-inputs text-center">
            <div v-for="(field, key) in inputFields"
              class="input-wrap col-2"
              :key="key"
              @click="selectInput(field.id)">
              <div class="col-6 relative">
                <span class="input-label">
                  {{ field.label }}
                </span>
                <input class="input"
                  :class="{ active: selectedInput === field.id }"
                  :autofocus="field.id === 'month'"
                  :ref="'field-' + field.id"
                  :value="field.value || field.defaultValue"
                  @focus="validateInput()"/>
                </div>
            </div>
          </div>
          <!-- Input numbers -->
          <div class="row number-inputs text-center">
            <div v-for="input in inputButtons"
              :key="input"
              class="number-input col-2">
              <Button
                @change="handleInputNumber(input)"
                :label="String(input)">
              </Button>
            </div>
            <div class="number-input col-2">
              <Button
                @change="resetSelectedInput()"
                :label="'C'">
              </Button>
            </div>
          </div>
          <!-- Cancel and Save -->
          <div class="row text-center">
            <div class="col-3 button-wrap">
              <Button class="cancel"
                type="terminal"
                :label="cancelLabel"
                @change="closePopup()">
              </Button>
            </div>
            <div class="col-3 button-wrap">
              <Button class="save"
                type="terminal"
                :label="saveLabel"
                @change="saveDate()">
              </Button>
            </div>
          </div>
        </div>
        </div>
    </div>
</template>

<script>
import parseDate from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import Button from './Button';

export default {
  name: 'DatepickerTouchPopup',
  components: {
    Button,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    dateLabel: {
      type: String,
      required: false,
      default: 'Date',
    },
    headerLabel: {
      type: String,
      required: false,
      default: 'Select date',
    },
    monthLabel: {
      type: String,
      required: false,
      default: 'Month',
    },
    dayLabel: {
      type: String,
      required: false,
      default: 'Day',
    },
    yearLabel: {
      type: String,
      required: false,
      default: 'Year',
    },
    cancelLabel: {
      type: String,
      required: false,
      default: 'Cancel',
    },
    saveLabel: {
      type: String,
      required: false,
      default: 'Save',
    },
  },
  methods: {
    openPopup() {
      this.stateShow = true;
    },
    handleInputNumber(num) {
      const input = this.inputFields[this.selectedInput];

      if (!input.value) {
        this.setInputValue(String(num));
      } else if (input.value.length < input.maxSize) {
        this.setInputValue(input.value + String(num));
      }
    },
    setInputValue(value) {
      this.inputFields[this.selectedInput].value = value;
    },
    selectInput(fieldId) {
      this.validateInput();
      this.selectedInput = fieldId;
      this.$refs[`field-${fieldId}`][0].focus();
    },
    validateInput() {
      const selectedInput = this.inputFields[this.selectedInput];
      const inputValue = String(selectedInput.value || selectedInput.defaultValue);
      const isValidDate = isValid(parseDate(this.createSelectedDateString(), 'yyyy-MM-dd', new Date()));

      if (isValidDate) {
        if (Number(inputValue) < 10 && inputValue.length < selectedInput.maxSize) {
          selectedInput.value = `0${inputValue}`;
        } else {
          selectedInput.value = inputValue;
        }

        selectedInput.defaultValue = selectedInput.value;
      }

      selectedInput.value = null;
    },
    resetSelectedInput() {
      this.inputFields[this.selectedInput].value = null;
    },
    createSelectedDateString() {
      const year = this.inputFields.year.value || this.inputFields.year.defaultValue;
      let month = this.inputFields.month.value || this.inputFields.month.defaultValue;
      let day = this.inputFields.day.value || this.inputFields.day.defaultValue;

      month = month < 10 && String(month).length < 2 ? `0${month}` : month;
      day = day < 10 && String(day).length < 2 ? `0${day}` : day;

      return `${year}-${month}-${day}`;
    },
    setDefaults() {
      let month = new Date().getMonth() + 1;
      let day = new Date().getDate();

      month = month < 10 ? `0${month}` : month;
      day = day < 10 ? `0${day}` : day;

      this.inputFields.year.defaultValue = new Date().getFullYear();
      this.inputFields.month.defaultValue = month;
      this.inputFields.day.defaultValue = day;
    },
    saveDate() {
      this.validateInput();
      this.selectedDate = this.createSelectedDateString();
      this.$emit('change', { date: this.selectedDate });
      this.closePopup();
    },
    closePopup() {
      this.$emit('closePopup');
      this.stateShow = false;
    },
  },
  data() {
    return {
      stateShow: false,
      inputFields: {
        month: {
          id: 'month',
          label: this.monthLabel,
          maxSize: 2,
          defaultValue: new Date().getMonth() + 1,
          value: null,
        },
        day: {
          id: 'day',
          label: this.dayLabel,
          maxSize: 2,
          defaultValue: new Date().getDate(),
          value: null,
        },
        year: {
          id: 'year',
          label: this.yearLabel,
          maxSize: 4,
          defaultValue: new Date().getFullYear(),
          value: null,
        },
      },
      inputButtons: [1, 2, 3, 4, 5, 6, 7, 8, 9, 0],
      selectedInput: 'month',
      selectedDate: null,
    };
  },
  mounted() {
    this.selectedDate = this.createSelectedDateString();
    this.setDefaults();
    this.$emit('setDate', { date: this.selectedDate });
  },
};

</script>

<style lang="scss" scoped>
  @import "../style/gravity/styleguide";
  @import '../style/icons';

  * {
    color: #FFFFFF;
  }

  body {
    font-size: 16px;
  }

  @for $i from 1 through 6 {
    .col-#{$i} {
      width: percentage($i / 6);
      display: inline-block;
    }
  }

  .relative {
    position: relative;
  }

  .text-center {
    text-align: center;
  }

  .row {
    width: 100%;
    position: relative;
    box-sizing: border-box;
  }

  .row::after {
      content: "";
      display: table;
      clear: both;
  }

  .datepicker-popup {
    width: 400px;
    margin: 10px 0;
    background-color: #242424;
    padding: 3px 10px;

    .date-wrap {
      display: inline-block;

      .selected-date {
        font-size: 1.4rem;
      }
    }

    .icon {
      float: right;
      font-size: 1.4rem;
      padding: 9px 0;
    }
  }

  .datepicker-wrapper {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: 115px auto;
    z-index: 999;

    .popup-container {
      width: 25%;
      position: relative;
      left: 37.5%;
      background: #323232;
      box-sizing: border-box;

      .header {
        margin-bottom: 0.975rem;

        .popup-title {
          padding-left: 12px;
          float: left;
          font-size: 1.5rem;
          margin: 10px 0;
        }
      }

      .input-wrap {
        display: inline-block;
        position: relative;
        margin-bottom: 1.95rem;
        box-sizing: border-box;

        .input {
          width: 100%;
          border: none;
          color: transparent;
          text-shadow: 0 0 0 #FFFFFF;
          background-color: #424242;
          line-height: 32px;
          padding-top: 12px;
          font-size: 1.25rem;
          text-align: center;
          box-sizing: border-box;

          &.active {
            background-color: #0066AE;
          }

          &:focus {
            border-color: #66AFE9;
            outline: none;
            box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(102, 175, 233, 0.6);
          }
        }

        .input-label {
          position: absolute;
          width: 100%;
          top: 0;
          left: 0;
          font-size: 0.9rem;
          padding-top: 2px;
        }

        &:first-of-type {
          padding-left: 11px;
          padding-right: 5px;
        }

        &:nth-of-type(2) {
          padding: 0 5px;
        }

        &:last-of-type {
          padding-left: 5px;
          padding-right: 11px;
        }
      }

      .number-inputs {
        margin-bottom: 1rem;

        .number-input {
          border: none;
          padding-bottom: 0.975rem;
          padding-left: 6px;
          padding-right: 6px;
          -webkit-box-sizing: border-box;
          -moz-box-sizing: border-box;
          box-sizing: border-box;

          &:nth-of-type(3n - 2) {
            padding-left: 11px;
          }

          &:nth-of-type(3n) {
            padding-right: 11px;
          }

          &:last-of-type {
            margin-right: 33%;
          }

          Button {
            width: 100%;
            display: block;
            padding: 0.65rem 0;
            background-color: #424242;
            font-size: 1.25rem;
          }
        }
      }

      .button-wrap {
        padding: 0.65rem 0;
        margin-bottom: 11px;
        font-size: 1.25rem;
        box-sizing: border-box;

        &:first-of-type {
          padding-left: 11px;
          padding-right: 5px;
        }

        &:last-of-type {
          padding-right: 11px;
          padding-left: 5px;
        }

        Button {
          width: 100%;

          &.cancel {
            background-color: #424242;
          }

          &.save {
            background-color: #0066AE;
          }
        }
      }
    }
  }

  .popup-close {
    padding-top: 15px;
    padding-right: 15px;
    font-size: 1.5rem;
    float: right;
  }

  .vertical-border:before {
    position: absolute;
    content: " ";
    width: 6px;
    height: 100%;
    margin-right: 5px;
    background-color: #2962ff;
  }
</style>
